import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { useUsersApi } from '../../services/usersApi'
import toast from 'react-hot-toast'
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  ChartBarIcon,
  TrophyIcon,
  CalendarIcon
} from '@heroicons/react/24/outline'

interface MemberAttendance {
  memberSince: number
  visits: number
  railbird: string
  firstName: string
  lastName: string
  userId: string
  attendance: { [year: number]: boolean }
}

export default function HistoricRosterPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<'name' | 'memberSince' | 'visits'>('visits')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const usersApi = useUsersApi()

  // Fetch all users data
  const { data: usersData, isLoading, error } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => usersApi.getUsers(),
    retry: 2,
  })

  // Generate years from current year back to 2007 (newest first)
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: currentYear - 2007 + 1 }, (_, i) => currentYear - i)

  // Handle error state
  useEffect(() => {
    if (error) {
      toast.error('Failed to load member data')
    }
  }, [error])

  // Transform users data into member attendance format
  const members: MemberAttendance[] = (usersData?.users || []).map(user => {
    // Parse attendance years and calculate stats
    const attendanceYears = user.attendanceYears || []
    const memberSince = user.joinedYear ? parseInt(user.joinedYear.toString()) : currentYear
    
    // Create attendance object for all years from 2007 to current
    const attendance: { [year: number]: boolean } = {}
    for (let year = 2007; year <= currentYear; year++) {
      attendance[year] = attendanceYears.includes(year.toString()) || attendanceYears.includes(year)
    }
    
    // Count total visits (true values in attendance)
    const visits = Object.values(attendance).filter(attended => attended).length

    return {
      memberSince,
      visits,
      railbird: `${user.lastName || ''}, ${user.firstName || ''}`.replace(/^, |, $/, ''),
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      userId: user.userId || '',
      attendance
    }
  })

  const filteredMembers = members
    .filter(member => 
      member.railbird.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.lastName.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let comparison = 0
      switch (sortBy) {
        case 'name':
          comparison = a.railbird.localeCompare(b.railbird)
          break
        case 'memberSince':
          comparison = a.memberSince - b.memberSince
          break
        case 'visits':
          comparison = a.visits - b.visits
          break
      }
      return sortOrder === 'asc' ? comparison : -comparison
    })

  const handleSort = (field: 'name' | 'memberSince' | 'visits') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('desc')
    }
  }

  const exportToCSV = () => {
    const headers = ['Member Since', '# of Visits', 'Railbird', ...years.map(y => y.toString())]
    const csvContent = [
      headers.join(','),
      ...filteredMembers.map(member => [
        member.memberSince,
        member.visits,
        `"${member.railbird}"`,
        ...years.map(year => member.attendance[year] ? '✓' : '')
      ].join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `railbirds-attendance-${new Date().getFullYear()}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-rb-dark py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
            <div className="h-64 bg-gray-300 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-rb-dark py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <Link
            to="/legacy"
            className="inline-flex items-center text-white hover:text-gray-200 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Legacy
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Historic Roster</h1>
              <p className="text-white/70">Member attendance records across all Keeneland events</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={exportToCSV}
                className="inline-flex items-center px-4 py-2 border border-white/30 text-white hover:bg-white/10 rounded-md transition-colors"
              >
                <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                Export CSV
              </button>
            </div>
          </div>
        </motion.div>

        {/* Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8 bg-white/10 backdrop-blur-sm rounded-xl p-6"
        >
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <FunnelIcon className="w-5 h-5 text-white" />
              <span className="text-white text-sm">Sort by:</span>
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-') as [typeof sortBy, typeof sortOrder]
                  setSortBy(field)
                  setSortOrder(order)
                }}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="visits-desc">Most Visits</option>
                <option value="visits-asc">Fewest Visits</option>
                <option value="name-asc">Name A-Z</option>
                <option value="name-desc">Name Z-A</option>
                <option value="memberSince-asc">Oldest Member</option>
                <option value="memberSince-desc">Newest Member</option>
              </select>
            </div>
          </div>
        </motion.div>

        {/* Roster Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('memberSince')}
                  >
                    Member Since {sortBy === 'memberSince' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('visits')}
                  >
                    # of Visits {sortBy === 'visits' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('name')}
                  >
                    Railbird {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  {years.map(year => (
                    <th key={year} className="px-2 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[50px]">
                      {year}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredMembers.map((member, index) => (
                  <tr key={`${member.railbird}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {member.memberSince}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {member.visits}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {member.railbird}
                    </td>
                    {years.map(year => (
                      <td key={year} className="px-2 py-4 whitespace-nowrap text-center text-sm">
                        {member.attendance[year] && (
                          <span className="text-green-600 dark:text-green-400 font-bold">✓</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Summary Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
            <div className="flex items-center">
              <ChartBarIcon className="w-8 h-8 text-primary-400 mr-3" />
              <div>
                <div className="text-2xl font-bold text-white">{filteredMembers.length}</div>
                <div className="text-white/70 text-sm">Total Members</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
            <div className="flex items-center">
              <TrophyIcon className="w-8 h-8 text-yellow-400 mr-3" />
              <div>
                <div className="text-2xl font-bold text-white">
                  {Math.max(...filteredMembers.map(m => m.visits))}
                </div>
                <div className="text-white/70 text-sm">Most Visits</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
            <div className="flex items-center">
              <CalendarIcon className="w-8 h-8 text-blue-400 mr-3" />
              <div>
                <div className="text-2xl font-bold text-white">
                  2007
                </div>
                <div className="text-white/70 text-sm">Founding Year</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
